// src/components/whitepaper/WhitepaperModal.tsx
//
// Modale de capture lead pour le téléchargement d'un livre blanc.
// - Split layout (mockup couverture à gauche + formulaire à droite sur desktop)
// - Soumission directe vers Supabase `leads` (source paramétrable)
// - Success state avec lien direct vers le PDF
// - Animation framer-motion (fade + slide)
// - Esc pour fermer, click sur backdrop pour fermer, X visible
// - Lock du scroll body quand ouverte
//
// Usage :
//   <WhitepaperModal
//     open={isOpen}
//     onClose={() => setOpen(false)}
//     onDownloaded={markDownloaded}
//     paper={{
//       slug: 'chiffres-cles-recrutement-2026',
//       title: '12 chiffres qui changent le recrutement en 2026',
//       subtitle: 'Le marché en clair pour les RH et les recruteurs.',
//       pdfUrl: 'https://...supabase.../livre-blanc.pdf',
//       coverUrl: 'https://...supabase.../cover.png',
//       pageCount: 28,
//     }}
//   />

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Mail,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  FileText,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import WhitepaperCoverIllustration from './WhitepaperCoverIllustration';

export interface WhitepaperConfig {
  /** Slug pour tracking (source = `whitepaper_${slug}`) */
  slug: string;
  /** Titre de la couverture / accroche modale */
  title: string;
  /** Sous-titre éditorial */
  subtitle: string;
  /** URL publique du PDF (Supabase Storage idéal) */
  pdfUrl: string;
  /** URL de la couverture/mockup (PNG/JPG) — visuel à gauche de la modale */
  coverUrl?: string;
  /** Nombre de pages affiché (badge crédibilité) */
  pageCount?: number;
  /** Sources affichées (badge crédibilité) — ex: "INSEE · APEC · DARES" */
  sources?: string;
  /** Focal number affiché dans le cercle gradient de la couverture
   *  (ex: "12", "0 €"). Si absent : auto-extrait du titre. */
  focalNumber?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Appelé quand le lead a téléchargé — pour marquer en localStorage côté caller */
  onDownloaded: () => void;
  paper: WhitepaperConfig;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

export default function WhitepaperModal({ open, onClose, onDownloaded, paper }: Props) {
  const [form, setForm] = useState<FormState>({ firstName: '', lastName: '', email: '', company: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);

  // Reset cover error quand paper change
  useEffect(() => {
    setCoverFailed(false);
  }, [paper.coverUrl]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Reset state quand la modale se ferme
  useEffect(() => {
    if (!open) {
      // garde le submitted state pendant la durée de l'animation puis reset
      const t = setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setForm({ firstName: '', lastName: '', email: '', company: '' });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  function setField<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const { firstName, lastName, email, company } = form;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !company.trim()) {
      setError('Prénom, nom, email et entreprise sont requis.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Format email invalide — utilisez votre email professionnel.');
      return;
    }

    setSubmitting(true);

    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanCompany = company.trim();

    try {
      // 1) Persiste le lead en DB Supabase (source de vérité)
      const { error: insertError } = await supabase.from('leads').insert([
        {
          first_name: cleanFirst,
          last_name: cleanLast,
          email: cleanEmail,
          company: cleanCompany,
          source: `whitepaper_${paper.slug}`,
        },
      ]);
      if (insertError) {
        console.error('[whitepaper] insert lead error:', insertError.message);
        setError(
          "Impossible d'enregistrer votre demande. Réessayez dans un instant ou contactez contact@recruteurs.io.",
        );
        return;
      }

      // 2) Notification email vers gerald@recruteurs.io via Edge Function
      //    Resend. Fire-and-forget : si la fonction tombe, le lead reste en
      //    DB et le visiteur ne voit aucune erreur.
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as
        | string
        | undefined;

      if (supabaseUrl && supabaseAnonKey) {
        fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/whitepaper-notify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
            apikey: supabaseAnonKey,
          },
          body: JSON.stringify({
            paper: { slug: paper.slug, title: paper.title },
            lead: {
              firstName: cleanFirst,
              lastName: cleanLast,
              email: cleanEmail,
              company: cleanCompany,
            },
            metadata: {
              submittedAt: new Date().toISOString(),
              userAgent:
                typeof navigator !== 'undefined' ? navigator.userAgent : '',
              referrer:
                typeof window !== 'undefined' ? window.location.pathname : '',
            },
          }),
          keepalive: true,
        }).catch((err) => {
          console.warn('[whitepaper] notify email failed:', err);
        });
      }

      // 3) Succès UX
      setSubmitted(true);
      onDownloaded();
    } catch (err) {
      console.error('[whitepaper] submit error:', err);
      setError('Une erreur est survenue. Réessayez ou contactez-nous.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whitepaper-modal-title"
        >
          {/* Backdrop */}
          <div
            onClick={onClose}
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            aria-hidden
          />

          {/* Modal card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-[1.75rem] bg-white shadow-2xl ring-1 ring-neutral-200"
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 ring-1 ring-neutral-200 hover:bg-white hover:ring-neutral-300 flex items-center justify-center text-neutral-500 hover:text-[#30475e] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.1fr]">
              {/* Colonne mockup couverture */}
              <div className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff] p-8 md:p-10 flex flex-col items-center justify-center min-h-[280px] md:min-h-[480px]">
                <div className="absolute -top-16 -left-16 w-48 h-48 bg-[#d03484]/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-[#d47254]/15 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 w-full max-w-xs">
                  {paper.coverUrl && !coverFailed ? (
                    <img
                      src={paper.coverUrl}
                      alt={`Couverture · ${paper.title}`}
                      className="w-full h-auto rounded-2xl shadow-2xl ring-1 ring-black/5"
                      loading="lazy"
                      onError={() => setCoverFailed(true)}
                    />
                  ) : (
                    // Illustration SaaS inline (fallback élégant, zéro asset requis)
                    <WhitepaperCoverIllustration
                      title={paper.title}
                      subtitle={paper.subtitle}
                      sources={paper.sources}
                      pageCount={paper.pageCount}
                      focalNumber={paper.focalNumber}
                    />
                  )}
                </div>
              </div>

              {/* Colonne formulaire / success state */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                {!submitted && (
                  <>
                    <div className="mb-6">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#d03484]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#d03484] mb-4">
                        <Sparkles className="w-3 h-3" />
                        Livre blanc gratuit
                      </span>
                      <h2
                        id="whitepaper-modal-title"
                        className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-[1.15] mb-3"
                      >
                        {paper.title}
                      </h2>
                      <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
                        {paper.subtitle}
                      </p>

                      {(paper.sources || paper.pageCount) && (
                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-500">
                          {paper.pageCount && (
                            <span className="inline-flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5 text-[#d03484]" />
                              {paper.pageCount} pages · PDF
                            </span>
                          )}
                          {paper.sources && (
                            <span className="inline-flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5 text-[#d03484]" />
                              Sources : {paper.sources}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                            Prénom <span className="text-[#d03484]">*</span>
                          </label>
                          <input
                            type="text"
                            value={form.firstName}
                            onChange={(e) => setField('firstName', e.target.value)}
                            autoComplete="given-name"
                            disabled={submitting}
                            className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                            Nom <span className="text-[#d03484]">*</span>
                          </label>
                          <input
                            type="text"
                            value={form.lastName}
                            onChange={(e) => setField('lastName', e.target.value)}
                            autoComplete="family-name"
                            disabled={submitting}
                            className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                          Email professionnel <span className="text-[#d03484]">*</span>
                        </label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => setField('email', e.target.value)}
                          autoComplete="email"
                          disabled={submitting}
                          placeholder="prenom.nom@entreprise.com"
                          className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] placeholder:text-neutral-400 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                          Nom de société <span className="text-[#d03484]">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.company}
                          onChange={(e) => setField('company', e.target.value)}
                          autoComplete="organization"
                          disabled={submitting}
                          className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                        />
                      </div>

                      {error && (
                        <div className="rounded-xl bg-red-50 ring-1 ring-red-200 px-4 py-2.5 text-sm text-red-700 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{error}</span>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 whitespace-nowrap"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            Recevoir le livre blanc par email
                            <Mail className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      <p className="text-[11px] text-neutral-500 text-center leading-relaxed">
                        En soumettant, vous acceptez d'être recontacté·e par recruteurs.io.
                        Vos données restent en France (Supabase EU). Désabonnement en 1 clic.
                      </p>
                    </form>
                  </>
                )}

                {submitted && (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] text-white shadow-lg shadow-[#d03484]/20 mb-5">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] mb-3 leading-tight">
                      Merci {form.firstName || 'à vous'} !{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                        Demande bien reçue.
                      </span>
                    </h2>
                    <p className="text-sm md:text-base text-neutral-600 leading-relaxed max-w-sm mx-auto mb-5">
                      Nous vous envoyons le livre blanc sur{' '}
                      <strong className="text-[#30475e] break-all">
                        {form.email}
                      </strong>
                      <strong> sous 24 h ouvrées</strong>, depuis{' '}
                      <strong className="text-[#30475e]">
                        contact@recruteurs.io
                      </strong>
                      .
                    </p>
                    <div className="rounded-2xl bg-[#fbfaf8] ring-1 ring-neutral-200 p-4 text-left max-w-sm mx-auto">
                      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest mb-2">
                        Pour ne pas le rater
                      </p>
                      <ul className="text-xs text-neutral-600 space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-[#d03484] flex-shrink-0">•</span>
                          Surveillez vos <strong>spams</strong> /{' '}
                          <strong>promotions</strong> (Gmail / Outlook)
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#d03484] flex-shrink-0">•</span>
                          Ajoutez <strong>contact@recruteurs.io</strong> à vos
                          contacts
                        </li>
                      </ul>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-5 py-2 text-sm font-semibold text-[#30475e] hover:bg-[#fbfaf8] transition-all whitespace-nowrap"
                    >
                      Continuer ma visite
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
